/** フォーム 1 項目の要約（人が読んで確認するための形）。 */
export type FormField = {
  readonly name: string;
  /** 表示ラベル。title が無ければプロパティ名。 */
  readonly label: string;
  readonly type: string;
  readonly required: boolean;
  /** enum の選択肢。無ければ null。 */
  readonly choices: readonly string[] | null;
};

type JsonObject = Readonly<Record<string, unknown>>;

/**
 * JSON Schema を項目一覧に要約する。
 *
 * スキーマそのものを読めば分かる情報だが、モデラーが「どんな入力を求めているか」を
 * 素早く確認できるようにする。入れ子・配列は展開しない（現行フォームは平坦で、
 * 必要になってから対応する）。
 */
export function summarizeFormSchema(schema: unknown): readonly FormField[] {
  if (!isObject(schema)) return [];

  const properties = schema.properties;
  if (!isObject(properties)) return [];

  const required = Array.isArray(schema.required)
    ? new Set(
        schema.required.filter((key): key is string => typeof key === "string"),
      )
    : new Set<string>();

  return Object.entries(properties).map(([name, raw]) => {
    const property = isObject(raw) ? raw : {};

    return {
      name,
      label: typeof property.title === "string" ? property.title : name,
      type: toTypeName(property.type),
      required: required.has(name),
      choices: toChoices(property.enum),
    };
  });
}

/**
 * nullable なスキーマは type が配列で来る（`["string","null"]`）。表示したいのは
 * 実際の型なので `null` 以外の最初の型を採る（`["null","string"]` の順もある）。
 */
function toTypeName(type: unknown): string {
  if (typeof type === "string") return type;
  if (Array.isArray(type)) {
    const named = type.find(
      (item): item is string => typeof item === "string" && item !== "null",
    );
    if (named !== undefined) return named;
    if (typeof type[0] === "string") return type[0];
  }
  return "unknown";
}

function toChoices(value: unknown): readonly string[] | null {
  if (!Array.isArray(value)) return null;
  return value.map((item) => String(item));
}

function isObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
